import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { message } from "antd";
import { v4 as uuidv4 } from "uuid";
import db from "../../../database";
import { getPricingRules, findPricingRuleById } from "../../../utils/pricingRulesCache";
import dayjs from "dayjs";
import { numberToWords } from "../../../utils/numberToWords";
import { pricingRuleController } from "../Core/PricingRules/pricingRuleController";
import { stock } from "../Core/stock.js";
import { CheckoutTotalManualDiscount } from "../Core/PricingRules/CheckoutTotalManualDiscount.js";
import { CheckoutFlatDiscount } from "../Core/PricingRules/CheckoutFlatDiscount.js";
import { CheckoutPercentageDiscount } from "../Core/PricingRules/CheckoutPercentageDiscount.js";
import messages from "../../../errors/messages.en-IN.json";
import useAuthStore from "../../../stores/useAuthStore";
import usePOSConfigStore from "../../../stores/usePOSConfigStore";
import useCartStore from "../../../stores/useCartStore";
import useAdvanceOrderStore from "../../../stores/useAdvanceOrderStore";
import { needsPopup } from "../../../lib/hooks/productPopupUtils";
import { useEventBus } from "../../../lib/components/EventBusProvider";
import { getIsInterState } from "../../../utils/taxUtils";

const ITEMS_PER_PAGE = 10;

const useCustomMix = ({
  cart,
  setCart,
  customMixItems,
  setCustomMixItems,
  groupName,
  setGroupName,
  groupQty,
  setGroupQty,
  existingGroupItems,
  setExistingGroupItems,
  selectedCustomMix,
  setSelectedCustomMix,
  orderType,
  setOrderType,
  posSaleTypes,
  setPosSaleTypes,
  currenciesData,
  setCurrenciesData,
  setProductStock,
  setShowCompleteSaleStock,
  setCompleteSaleStockData,
  processBillDiscounts,
  setLoader,
  processTotalManualDiscount,
}) => {
  const [productData, setProductData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [manualDiscountTypes, setManualDiscountTypes] = useState([]);
  const [stockFailedItems, setStockFailedItems] = useState([]);

  const tillData = useAuthStore.getState().tillData;
  const saleTypeData = usePOSConfigStore.getState().saleTypeData || {};
  const taxIncludeFlag =
    saleTypeData?.customer?.istaxincluded === "Y"
      ? "Y"
      : tillData.tillAccess.csBunit.isTaxIncluded
        ? tillData.tillAccess.csBunit.isTaxIncluded
        : "Y";
  const precision = tillData.tillAccess.csBunit.currencies[0].prcPrecision;
  const { dispatchWeight } = useEventBus();

  // Clear stock failure highlights when items change (user adjusted quantities)
  useEffect(() => {
    if (stockFailedItems.length > 0) {
      setStockFailedItems([]);
    }
  }, [customMixItems]);

  // Load products on mount
  useEffect(() => {
    (async () => {
      // CW Browser (SQLite) note: .filter() on a DB collection cannot carry a
      // JS closure over IPC. Always toArray() first and filter in plain JS.
      const allProducts = await db.products.toArray();
      const productsList = allProducts.filter(
        (product) => product.isactive == true && product.productSegment !== "BG"
      );
      setProductData(productsList);
    })();
  }, []);

  // Load manual discount types
  useEffect(() => {
    const pr = getPricingRules();
    const bunitId = tillData.tillAccess.csBunit.csBunitId;
    const manualPricingRules = pr.filter((rule) => {
      if (rule.manualDiscount === "Y") {
        if (rule.excludeBusinessUnits === "Y") {
          if (rule.mPricingBUnits.length === 0) return rule;
          const index = rule.mPricingBUnits.findIndex((Bunit) => Bunit.mBunitPricingId !== bunitId);
          if (index !== -1) return rule;
        } else {
          const index = rule.mPricingBUnits.findIndex((Bunit) => Bunit.mBunitPricingId === bunitId);
          if (index >= 0) return rule;
        }
      }
    });
    setManualDiscountTypes([...manualPricingRules]);
  }, []);

  // Init currencies and sale types
  useEffect(() => {
    const totalRecords = 12;
    let filledCurrencies = [...currenciesData];
    while (filledCurrencies.length < totalRecords) {
      filledCurrencies.push({ id: `placeholder-${filledCurrencies.length}`, value: 999999 });
    }
    setCurrenciesData(filledCurrencies.sort((a, b) => b.value - a.value));
    db.posSaletypes.toArray().then((saleType) => {
      setPosSaleTypes([...saleType]);
      const saleIndex = saleType.findIndex((st) => st.cwrSaletype.isdefault === "Y");
      setOrderType(saleType[saleIndex]);
    });
  }, []);

  // Filter products based on search
  useEffect(() => {
    // Trim before lowercasing — without this, a stray leading/trailing space
    // from the keyboard or an IME composition fragment makes the substring
    // check miss every product (e.g. "moti" returned 0 results until a space
    // was added). (Tab8 issue #10)
    const lowerQuery = (searchQuery || "").trim().toLowerCase();
    let filtered = productData.filter((item) => {
      const isValidSegment = item.productSegment !== "CB";
      if (!lowerQuery) return isValidSegment;
      const nameMatch = (item.name || "").toLowerCase().includes(lowerQuery);
      const codeMatch = lowerQuery.length >= 3 && (item.value || "").toLowerCase().includes(lowerQuery);
      const upcMatch = lowerQuery.length >= 3 && item.upc && item.upc.toLowerCase().includes(lowerQuery);
      return isValidSegment && (nameMatch || codeMatch || upcMatch);
    });
    if (lowerQuery) {
      filtered.sort((a, b) => {
        const aName = (a.name || "").toLowerCase();
        const bName = (b.name || "").toLowerCase();
        const aNameMatch = aName.includes(lowerQuery);
        const bNameMatch = bName.includes(lowerQuery);
        if (aNameMatch && !bNameMatch) return -1;
        if (!aNameMatch && bNameMatch) return 1;
        const aExact = aName === lowerQuery;
        const bExact = bName === lowerQuery;
        if (aExact && !bExact) return -1;
        if (!aExact && bExact) return 1;
        const aStarts = aName.startsWith(lowerQuery);
        const bStarts = bName.startsWith(lowerQuery);
        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;
        return 0;
      });
    }
    setFilteredProducts(filtered);
  }, [searchQuery, productData]);

  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const startIndex = currentPage * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredProducts.slice(startIndex, endIndex);
  }, [filteredProducts, currentPage]);

  const handlePreviousPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  }, [totalPages]);

  // --- Integrated weight popup state (ProductsPopup-style -- for products WITHOUT alternate UOM) ---
  const [integratedWeightProduct, setIntegratedWeightProduct] = useState(null);
  const [integratedWeightPopupOpen, setIntegratedWeightPopupOpen] = useState(false);
  const integratedWeightCallbackRef = useRef(null);
  const integratedWeightInputRef = useRef(null);
  const isAdvanceOrderEnabled = useAdvanceOrderStore.getState().isAdvanceOrderEnabled;
  const isPreOrderPage = window.location.pathname.includes("/preorder");

  const openWeightPopupForGrouped = useCallback(
    (product, callback) => {
      const existing = customMixItems.find((i) => i.mProductId === product.mProductId);
      const initWeight = existing?.weight || product.weight || 0;
      const initRaw = existing?.rawWeight || product.rawWeight || initWeight.toString();
      const hasAlternateUom = product?.alternateUom?.length > 0 && product?.showAlternateUomInPos === "Y";

      if (hasAlternateUom) {
        const productWithInit = {
          ...product,
          uom: product.csUomId || product.uom || null,
          uom_name: product.uomName || product.uom_name || "",
          weight: initWeight,
          rawWeight: initRaw,
        };
        dispatchWeight(productWithInit, () => {}, null, () => {}, (updatedProduct) => {
          if (updatedProduct) callback(updatedProduct);
        });
      } else {
        setIntegratedWeightProduct({ ...product, weight: initWeight, rawWeight: initRaw });
        setIntegratedWeightPopupOpen(true);
        integratedWeightCallbackRef.current = callback;
        setTimeout(() => integratedWeightInputRef.current?.focus?.(), 100);
      }
    },
    [customMixItems, dispatchWeight]
  );

  // --- Integrated weight popup handlers (ProductsPopup-style) ---
  const handleIntegratedWeightTotalQty = useCallback((value) => {
    if (!integratedWeightProduct) return;
    const currentWeight = integratedWeightProduct.weight?.toString() || "0";
    let newWeight;
    if (value === "x") {
      newWeight = currentWeight.slice(0, -1) || "0";
    } else if (value === "clear") {
      newWeight = "0";
    } else {
      newWeight = currentWeight === "0" ? value : currentWeight + value;
    }
    setIntegratedWeightProduct({ ...integratedWeightProduct, weight: newWeight, rawWeight: newWeight });
  }, [integratedWeightProduct]);

  const handleIntegratedWeightInputChange = useCallback((value) => {
    if (!integratedWeightProduct) return;
    const cleaned = value.replace(/[^0-9.]/g, "");
    setIntegratedWeightProduct({ ...integratedWeightProduct, weight: cleaned, rawWeight: cleaned });
  }, [integratedWeightProduct]);

  const handleIntegratedWeightConfirm = useCallback(() => {
    if (!integratedWeightProduct) return;
    const isStrictQty = usePOSConfigStore.getState().posConfig?.requireQtyBeforeAddingItem === "Y";
    if (isStrictQty && Number(integratedWeightProduct.weight || 0) <= 0) return;
    const updatedProduct = {
      ...integratedWeightProduct,
      weight: isStrictQty ? integratedWeightProduct.weight : (Number(integratedWeightProduct.weight) === 0 ? "1" : integratedWeightProduct.weight),
      rawWeight: integratedWeightProduct.rawWeight || integratedWeightProduct.weight?.toString(),
      unit: integratedWeightProduct?.uomData?.[0]?.name?.toLowerCase() || "kg",
    };
    integratedWeightCallbackRef.current?.(updatedProduct);
    setIntegratedWeightProduct(null);
    setIntegratedWeightPopupOpen(false);
    integratedWeightCallbackRef.current = null;
  }, [integratedWeightProduct]);

  const handleIntegratedWeightCancel = useCallback(() => {
    integratedWeightCallbackRef.current?.(null);
    setIntegratedWeightProduct(null);
    setIntegratedWeightPopupOpen(false);
    integratedWeightCallbackRef.current = null;
  }, []);

  const buildMergedCartForStock = useCallback(
    (updatedMixItems) => {
      const grouped = {};
      // Skip the combo being edited so its children don't double-count (#13)
      const editingLineId = existingGroupItems ? selectedCustomMix?.lineId : null;
      (cart?.items || []).forEach((item) => {
        if (editingLineId && item.lineId === editingLineId) return;
        if (Array.isArray(item.children) && item.children.length > 0) {
          item.children.forEach((child) => {
            const key = `${child.productId || child.mProductId}_${child.mBatchId || "null"}`;
            if (!grouped[key]) {
              grouped[key] = { ...child, weight: Number(child.weight) };
            } else {
              grouped[key].weight += Number(child.weight);
            }
          });
        } else {
          const key = `${item.productId || item.mProductId}_${item.mBatchId || "null"}`;
          if (!grouped[key]) {
            grouped[key] = { ...item, weight: Number(item.weight) };
          } else {
            grouped[key].weight += Number(item.weight);
          }
        }
      });
      updatedMixItems.forEach((item) => {
        const key = `${item.mProductId}_${item.mBatchId || "null"}`;
        if (!grouped[key]) {
          grouped[key] = { ...item, productId: item.mProductId, weight: Number(item.weight) };
        } else {
          grouped[key].weight += Number(item.weight);
        }
      });
      return { ...cart, items: Object.values(grouped) };
    },
    [cart, existingGroupItems, selectedCustomMix]
  );

  const runStockCheck = useCallback(
    async (updatedMixItems, opts = {}) => {
      const tempCart = buildMergedCartForStock(updatedMixItems);
      const isPreOrderPage = window.location.pathname.includes("/preorder");
      if (navigator.onLine && tillData?.tillAccess?.csBunit?.allowNegativeStock !== "Y" && !isPreOrderPage) {
        const stockCheckResult = await stock({
          cart: tempCart,
          setCart,
          tillData,
          setProductStock,
          isFinalValidation: false,
          isCompleteSale: opts.isCompleteSale || false,
          setShowCompleteSaleStock: opts.isCompleteSale ? setShowCompleteSaleStock : undefined,
          setCompleteSaleStockData: opts.isCompleteSale ? setCompleteSaleStockData : undefined,
          customMixHandler: true,
        });
        // stock() returns { passed, failedItems } for customMixHandler, or boolean
        if (typeof stockCheckResult === "object" && stockCheckResult !== null && !stockCheckResult.passed) {
          return stockCheckResult; // { passed: false, failedItems: [...] }
        }
        // true or false — normalize to object for consistency
        return stockCheckResult === true ? { passed: true, failedItems: [] } : { passed: false, failedItems: [] };
      }
      return { passed: true, failedItems: [] };
    },
    [buildMergedCartForStock, tillData, setCart, setProductStock, setShowCompleteSaleStock, setCompleteSaleStockData]
  );

  const handleIncrease = useCallback(
    (product) => {
      if (needsPopup(product)) {
        openWeightPopupForGrouped(product, (updatedProduct) => {
          if (!updatedProduct) return;
          let newValue = updatedProduct?.rawWeight ?? updatedProduct?.weight?.toString() ?? "";
          if (!newValue || newValue === "0") newValue = "1";
          handleQuantityChange(updatedProduct, newValue);
        });
        return;
      }
      setCustomMixItems((prev) => {
        const isInCart = prev.find((item) => item.mProductId === product.mProductId);
        return isInCart
          ? prev.map((item) =>
              item.mProductId === product.mProductId
                ? { ...item, weight: Number(item.weight) + 1, rawWeight: (Number(item.weight) + 1).toString() }
                : item
            )
          : [
              ...prev,
              { ...product, weight: 1, rawWeight: "1", lineId: uuidv4().replace(/-/g, "").toUpperCase() },
            ];
      });
    },
    [openWeightPopupForGrouped, setCustomMixItems]
  );

  const handleDecrease = useCallback(
    (product) => {
      if (needsPopup(product)) {
        openWeightPopupForGrouped(product, (updatedProduct) => {
          if (!updatedProduct) return;
          const newValue = updatedProduct.rawWeight || updatedProduct.weight?.toString();
          handleQuantityChange(updatedProduct, newValue);
        });
        return;
      }
      setCustomMixItems((prev) => {
        const isInCart = prev.find((item) => item.mProductId === product.mProductId);
        if (isInCart && isInCart.weight > 1) {
          return prev.map((item) =>
            item.mProductId === product.mProductId
              ? { ...item, weight: Number(item.weight) - 1, rawWeight: (Number(item.weight) - 1).toString() }
              : item
          );
        } else if (isInCart && isInCart.weight <= 1) {
          return prev.filter((item) => item.mProductId !== product.mProductId);
        }
        return prev;
      });
    },
    [openWeightPopupForGrouped, setCustomMixItems]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      setCustomMixItems((prev) => prev.filter((item) => item.lineId !== itemToDelete.lineId));
    },
    [setCustomMixItems]
  );

  const handleQuantityChange = useCallback(
    (product, value) => {
      const isDecimal = product?.uomData?.[0]?.decimal === "Y";
      let newValue = value;
      if (newValue === "") newValue = "0";

      const currentWeight = product.rawWeight || (product.weight !== undefined ? product.weight.toString() : "0");
      const isInitialZero = currentWeight === "0";
      if (isInitialZero && /\d/.test(newValue) && !newValue.startsWith("0.")) {
        newValue = newValue.replace(/^0+/, "");
        if (newValue === "") newValue = "0";
      }

      const isValidInput = isDecimal ? /^(\d*\.?\d{0,3}|\d*\.|\d*\.\d{0,3}\.)$/.test(newValue) : /^\d*$/.test(newValue);
      if (!isValidInput) return;

      const parsedValue =
        newValue === "0" || newValue === "." || newValue === "0." || newValue === "0.0" || newValue === "0.00" ? 0 : parseFloat(newValue) || 0;

      if (parsedValue < 0) {
        message.error(`${messages.info.quantity_not_negative}`);
        return;
      }

      setCustomMixItems((prev) => {
        const isInCart = prev.find((item) => item.mProductId === product.mProductId);

        if (isInCart) {
          if (parsedValue === 0 && ![".", "0.", "0.0", "0.00"].includes(newValue)) {
            return prev.filter((item) => item.mProductId !== product.mProductId);
          }
          return prev.map((item) =>
            item.mProductId === product.mProductId
              ? { ...item, weight: isDecimal ? parsedValue : Math.floor(parsedValue), rawWeight: newValue }
              : item
          );
        } else if (parsedValue > 0 || [".", "0.", "0.0", "0.00"].includes(newValue)) {
          return [
            ...prev,
            {
              ...product,
              weight: isDecimal ? parsedValue : Math.floor(parsedValue),
              rawWeight: newValue,
              lineId: uuidv4().replace(/-/g, "").toUpperCase(),
            },
          ];
        }
        return prev;
      });
    },
    [setCustomMixItems]
  );

  const handleGroupQtyChange = useCallback(
    (newGroupQty) => {
      setGroupQty(newGroupQty);
    },
    [setGroupQty]
  );

  const buildCartObject = useCallback(() => {
    const cartObj = cart || { items: [], total: 0, tax: 0, subTotal: 0 };
    const childrenID = uuidv4().replace(/-/g, "").toUpperCase();

    const totalTaxAmount = customMixItems.reduce((sum, item) => {
      const taxAmount =
        taxIncludeFlag === "Y"
          ? (item.sunitprice - item.sunitprice / (1 + item.taxRate / 100)) * (groupQty || 1) * item.weight
          : item.sunitprice * (item.taxRate / 100) * (groupQty || 1) * item.weight;
      return sum + taxAmount;
    }, 0);

    const totalRealPrice = customMixItems.reduce((sum, item) => sum + (item.sunitprice || 0), 0);

    const cartTotal = parseFloat(customMixItems.reduce((sum, item) => {
      const sp = parseFloat(item.sunitprice);
      const mrp = sp * item.weight * groupQty;
      const tax = taxIncludeFlag === "Y" ? mrp - mrp / (1 + item.taxRate / 100) : (mrp * item.taxRate) / 100;
      const nettotal = taxIncludeFlag === "Y" ? mrp : mrp + tax;
      return sum + nettotal;
    }, 0).toFixed(precision));

    const children = customMixItems.map((item) => {
      const sp = parseFloat(item.sunitprice);
      const mrp = sp * item.weight * (groupQty || 1);
      const tax = taxIncludeFlag === "Y" ? mrp - mrp / (1 + item.taxRate / 100) : (mrp * item.taxRate) / 100;
      const nettotalFinal = parseFloat(taxIncludeFlag === "Y" ? mrp : mrp + tax);
      const nettotalFixed = parseFloat(nettotalFinal);
      const unitPrice = isFinite(nettotalFinal / (item.weight * (groupQty || 1)))
        ? taxIncludeFlag === "Y"
          ? nettotalFinal / (item.weight * (groupQty || 1)) / (1 + item.taxRate / 100)
          : item?.sunitprice
        : 0;
      const lineTax = item.slistprice * (1 + item.taxRate / 100);
      const stdTax = item.sunitprice * (1 + item.taxRate / 100);
      const gross_list = taxIncludeFlag === "Y" ? item.slistprice : parseFloat(parseFloat(lineTax).toFixed(precision));
      let netStd = parseFloat(item.sunitprice - item.sunitprice / (1 + item.taxRate / 100));
      const netStdItem =
        taxIncludeFlag === "Y" ? (item.sunitprice > 0 ? parseFloat(parseFloat(item.sunitprice - netStd).toFixed(precision)) : 0) : (item.sunitprice ?? 0);
      const grossStd = taxIncludeFlag === "Y" ? item.sunitprice : stdTax;
      const netList =
        taxIncludeFlag === "Y"
          ? item.slistprice - parseFloat(parseFloat(item.slistprice - item.slistprice / (1 + item.taxRate / 100)).toFixed(precision))
          : item.slistprice;

      return {
        ...item,
        productId: item.mProductId,
        lineId: uuidv4().replace(/-/g, "").toUpperCase(),
        childrenId: childrenID,
        groupedItems: true,
        discount: 0,
        tax: item.cTaxId,
        nettotal: parseFloat(parseFloat(nettotalFinal).toFixed(precision)),
        realPrice: item.sunitprice || 0,
        weight: parseFloat((item.weight * groupQty).toFixed(item?.uomData?.[0]?.stdprecision ?? 2)),
        rawWeight: item.rawWeight,
        isQtyDesimal: item?.uomData?.[0]?.stdprecision ?? 2,
        isDecimalQty: item?.uomData?.[0]?.decimal === "Y",
        taxAmount: parseFloat(parseFloat(tax).toFixed(precision)),
        linetax: parseFloat(parseFloat(tax).toFixed(precision)),
        linenet: nettotalFixed
          ? parseFloat((parseFloat(parseFloat(nettotalFixed).toFixed(precision)) - parseFloat(parseFloat(tax).toFixed(precision))).toFixed(precision))
          : 0,
        linegross: parseFloat(parseFloat(nettotalFixed).toFixed(precision)) || 0,
        linestd: nettotalFixed ? parseFloat(parseFloat(grossStd * item.weight).toFixed(precision)) : 0,
        netunit: nettotalFixed ? parseFloat(parseFloat(unitPrice).toFixed(precision)) : 0,
        listPrice: nettotalFixed ? item.slistprice : 0,
        grossunit: nettotalFixed ? parseFloat(parseFloat(unitPrice).toFixed(precision)) * (item.isReturn ? -1 : 1) : 0,
        grosslist: nettotalFixed ? gross_list : 0,
        grossstd: nettotalFixed ? parseFloat(parseFloat(grossStd).toFixed(precision)) : 0,
        netStd: netStdItem,
        netList: nettotalFixed ? parseFloat(parseFloat(netList).toFixed(precision)) : 0,
        actualWeight: item.weight * (groupQty || 1),
        baseQty: item.weight,
        mBatchId: null,
      };
    });

    const obj = {
      addNewLine: "N",
      batchedForSale: "N",
      batchedForStock: "N",
      batchedProduct: "N",
      batchno: null,
      bunitName: "",
      csBunitId: null,
      customAttributes: [],
      cwrMenuTimeslot: [],
      cwrPcatalogueId: "75A987950FAC4571985E4FDAADC1366F",
      description: "",
      discount: 0,
      discountName: "",
      foodType: "",
      itemType: "",
      grosslist: 0,
      grossstd: 0,
      grossunit: 0,
      hsncode: "",
      imageUrl: "",
      imageurl: "",
      isDecimal: false,
      isDecimalQty: false,
      isManualQty: false,
      isPromoApplicable: false,
      // Parent custom-mix item is synthetic (no own UOM); children carry actual precision
      isQtyDesimal: 2,
      isReturn: false,
      key: 0,
      lineId: uuidv4().replace(/-/g, "").toUpperCase(),
      childrenId: childrenID,
      linegross: 0,
      linenet: 0,
      linetax: 0,
      listPrice: 0,
      listprice: 0,
      mBatchId: null,
      mPricingruleId: null,
      multiPrice: "N",
      name: groupName,
      name2: "",
      netList: 0,
      netStd: 0,
      nettotal: cartTotal,
      netunit: 0,
      newCustomAttributes: [],
      order: "N",
      originalPrice: totalRealPrice,
      priceList: [],
      primaryOrderLine: null,
      productAddons: [],
      productCategoryName: "",
      productId: uuidv4().replace(/-/g, "").toUpperCase(),
      productManufacturerId: null,
      productionCenter: "",
      realPrice: totalRealPrice,
      returnQty: null,
      salePrice: 0,
      salesRepId: null,
      salesRepName: "",
      selectedAddons: [],
      shelfLife: "",
      shortDescription: "",
      showAddons: "N",
      sunitprice: cartTotal,
      taxAmount: parseFloat(parseFloat(totalTaxAmount).toFixed(precision)),
      taxCategory: "",
      taxFlag: "N",
      taxName: "",
      type: "I",
      unitPrice: 0,
      unitTax: 0,
      upc: "",
      upcPricingRule: null,
      value: "",
      weight: groupQty,
      children,
    };

    return { cartObj, obj };
  }, [cart, customMixItems, groupQty, groupName, taxIncludeFlag, precision]);

  const handleSubmit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      if (customMixItems.length === 0) {
        message.warning("Please add at least one item to the combo before submitting");
        return false;
      }
      if (customMixItems.length > 0) {
        if (!groupName) {
          message.warning(`${messages.info.groupname_required}`);
          return false;
        }
        // Validate at least one item has qty > 0
        const hasValidItem = customMixItems.some((item) => Number(item.weight) > 0);
        if (!hasValidItem) {
          message.warning("Please add at least one item with quantity greater than 0");
          return false;
        }

        // Validate stock before submitting — scale items by groupQty for accurate check
        const scaledItems = customMixItems.map((item) => ({
          ...item,
          weight: parseFloat((item.weight * (groupQty || 1)).toFixed(item?.uomData?.[0]?.stdprecision ?? 2)),
        }));
        const stockResult = await runStockCheck(scaledItems);
        if (!stockResult.passed) {
          const failedItems = stockResult.failedItems || [];
          // Map failed productIds back to custom mix items for visual highlighting
          const failedProductIds = failedItems.map((f) => f.productId);
          setStockFailedItems(failedProductIds);
          if (failedItems.length > 0) {
            const failedNames = failedItems.map((f) => {
              // Try to find the name from customMixItems first (more reliable)
              const mixItem = customMixItems.find(
                (m) => (m.value || m.mProductId) === f.productId
              );
              const name = mixItem?.name || f.name || "Unknown";
              const available = Number(f.availableQty) || 0;
              return `${name} (available: ${available})`;
            });
            message.warning(`Insufficient stock for: ${failedNames.join(", ")}`, 5);
          } else {
            message.warning("Insufficient stock for one or more items in the mix");
          }
          return false;
        }
        // Clear any previous stock failure highlights on successful check
        setStockFailedItems([]);

        const { cartObj, obj } = buildCartObject();
        const prevSummary = cartObj.itemsSummery;

        let updatedItems = [...cartObj.items];
        if (existingGroupItems) {
          const existingGroupIndex = updatedItems.findIndex((item) => item.lineId === selectedCustomMix.lineId);
          if (existingGroupIndex !== -1) {
            const existingItem = updatedItems[existingGroupIndex];
            updatedItems[existingGroupIndex] = {
              ...obj,
              lineId: existingItem.lineId,
              mPricingruleId: existingItem.mPricingruleId,
              discount: existingItem.discount,
              discountName: existingItem.discountName,
              discountType: existingItem.discountType,
              approval: existingItem.approval,
              allDiscounts: existingItem.allDiscounts,
            };
          } else {
            updatedItems.push(obj);
          }
        } else {
          updatedItems.push(obj);
        }

        if (cart.couponInput?.length > 0) {
          cart.couponInput = cart.couponInput?.filter((coupon) => obj.couponInput !== coupon);
        }

        const isInterState = getIsInterState(cart);
        const allItems = (updatedItems || []).flatMap((item) =>
          Array.isArray(item.children) && item.children.length > 0 ? item.children : [item]
        );

        const groupedItems = allItems.reduce((acc, item) => {
          const key = item.tax?.rate ?? item.taxRate ?? "0";
          acc[key] = acc[key] || [];
          acc[key].push(item);
          return acc;
        }, {});

        const groupedItemsByTaxRate = Object.entries(groupedItems).map(([taxRate, items]) => {
          const totalTaxAmount = items.reduce((sum, item) => sum + parseFloat(parseFloat(item.taxAmount || 0).toFixed(precision)), 0);
          const totalNetTotal = items.reduce((sum, item) => sum + parseFloat(parseFloat(item.nettotal || 0).toFixed(precision)), 0);
          const totalSgstAmount = items.reduce(
            (sum, item) => sum + parseFloat(parseFloat(item.sgst || item.tax?.sgst || parseFloat(item.taxAmount || 0) / 2).toFixed(precision)),
            0
          );
          const totalCgstAmount = items.reduce(
            (sum, item) => sum + parseFloat(parseFloat(item.cgst || item.tax?.cgst || parseFloat(item.taxAmount || 0) / 2).toFixed(precision)),
            0
          );
          return {
            taxRate,
            totalLinenet: parseFloat(parseFloat(totalNetTotal - totalTaxAmount).toFixed(precision)),
            totalLinetax: parseFloat(parseFloat(totalTaxAmount).toFixed(precision)),
            totalLinegross: parseFloat(parseFloat(totalNetTotal).toFixed(precision)),
            totalSgst: isInterState ? 0 : parseFloat(parseFloat(totalSgstAmount).toFixed(precision)),
            totalCgst: isInterState ? 0 : parseFloat(parseFloat(totalCgstAmount).toFixed(precision)),
            totalIgst: isInterState ? parseFloat(parseFloat(totalTaxAmount).toFixed(precision)) : 0,
          };
        });

        // Calculate item summary
        const itemSummery = { decimalItems: { totalQty: 0, totalAmount: 0 }, nonDecimalItems: { totalQty: 0, totalAmount: 0 }, itemsQty: 0, items: 0 };
        customMixItems.forEach((item) => {
          if (Array.isArray(item.children) && item.children.length > 0) {
            item.children.forEach((child) => {
              const isDecimal = child.uomData?.[0]?.decimal === "Y" || child.isDecimal;
              const summaryKey = isDecimal ? "decimalItems" : "nonDecimalItems";
              const itemWeight = child.weight * (groupQty || 1);
              const netTotal = parseFloat(parseFloat(child.sunitprice * itemWeight).toFixed(precision));
              itemSummery.itemsQty += itemWeight;
              itemSummery.items += 1;
              itemSummery[summaryKey].totalQty += itemWeight;
              itemSummery[summaryKey].totalAmount += netTotal;
            });
          } else {
            const isDecimal = item.uomData?.[0]?.decimal === "Y" || item.isDecimal;
            const summaryKey = isDecimal ? "decimalItems" : "nonDecimalItems";
            const itemWeight = item.weight * (groupQty || 1);
            const netTotal = parseFloat(parseFloat(item.sunitprice * itemWeight).toFixed(precision));
            itemSummery.itemsQty += itemWeight;
            itemSummery.items += 1;
            itemSummery[summaryKey].totalQty += itemWeight;
            itemSummery[summaryKey].totalAmount += netTotal;
          }
        });

        const mergedItemSummery = {
          decimalItems: {
            totalQty: (prevSummary?.decimalItems?.totalQty || 0) + itemSummery.decimalItems.totalQty,
            totalAmount: (prevSummary?.decimalItems?.totalAmount || 0) + itemSummery.decimalItems.totalAmount,
          },
          nonDecimalItems: {
            totalQty: (prevSummary?.nonDecimalItems?.totalQty || 0) + itemSummery.nonDecimalItems.totalQty,
            totalAmount: (prevSummary?.nonDecimalItems?.totalAmount || 0) + itemSummery.nonDecimalItems.totalAmount,
          },
          itemsQty: (prevSummary?.itemsQty || 0) + itemSummery.itemsQty,
          items: (prevSummary?.items || 0) + itemSummery.items,
        };

        // Calculate total STD
        const totalSTD = cartObj.items.reduce((total, item) => {
          const calculateLineSTD = (data) => {
            const sp = parseFloat(data.sunitprice);
            const mrp = sp * data.weight * (groupQty || 1);
            const tax = taxIncludeFlag === "Y" ? mrp - mrp / (1 + data.taxRate / 100) : (mrp * data.taxRate) / 100;
            const nettotalFinal = parseFloat(taxIncludeFlag === "Y" ? mrp : mrp + tax);
            const stdTax = data.sunitprice * (1 + data.taxRate / 100);
            const grossStd = taxIncludeFlag === "Y" ? data.sunitprice : stdTax;
            return nettotalFinal ? grossStd * data.weight : 0;
          };
          if (Array.isArray(item.children)) {
            return total + item.children.reduce((childTotal, child) => childTotal + calculateLineSTD(child), 0);
          }
          return total + calculateLineSTD(item);
        }, 0);

        const finalCartTotal = updatedItems.reduce((sum, item) => sum + (item.nettotal || 0), 0);
        const finalCartTax = updatedItems.reduce((sum, item) => sum + (parseFloat(parseFloat(item.taxAmount).toFixed(precision)) || 0), 0);
        const finalCartSubTotal = finalCartTotal - finalCartTax;
        const totalLineStd = customMixItems.reduce((sum, item) => {
          const stdTax = item.sunitprice * (1 + (item.taxRate || 0) / 100);
          const grossStd = taxIncludeFlag === "Y" ? item.sunitprice : stdTax;
          return sum + parseFloat(parseFloat(grossStd * item.weight).toFixed(precision));
        }, 0);

        const finalTotalQty = updatedItems.reduce((sum, item) => {
          if (Array.isArray(item.children) && item.children.length > 0) {
            return sum + item.children.reduce((cSum, c) => cSum + (Number(c.weight) || 0), 0);
          }
          return sum + (Number(item.weight) || 0);
        }, 0);

        let finalCartObj = {
          ...cartObj,
          amountInWords: numberToWords(finalCartTotal),
          items: updatedItems,
          total: finalCartTotal,
          totalQty: finalTotalQty,
          tax: finalCartTax,
          subTotal: finalCartSubTotal,
          groupItemsFlag: true,
          totalStd: parseFloat(parseFloat(totalSTD + totalLineStd).toFixed(precision)),
          groupedItemsByTaxRate,
          itemsSummery: mergedItemSummery,
        };

        let updatedCart;
        if (finalCartObj.items?.length > 0) {
          let orderTimeDetails = usePOSConfigStore.getState().orderTimeDetails || { orderStartTime: "", orderEndTime: "", paymentStartTime: "" };
          orderTimeDetails = {
            ...orderTimeDetails,
            paymentStartTime: "",
            orderStartTime: orderTimeDetails.orderStartTime !== "" ? orderTimeDetails.orderStartTime : dayjs(new Date()).format("DD-MM-YYYY HH:mm:ss"),
          };
          usePOSConfigStore.getState().setOrderTimeDetails(orderTimeDetails);
          updatedCart = obj.isReturn ? finalCartObj : await pricingRuleController(obj, finalCartObj, cart, setCart);
        }

        if (updatedCart?.couponInput?.length > 0 && cart?.statusType !== "layAway") {
          await Promise.all(
            updatedCart.couponInput.map(async (coupon) => {
              updatedCart = await pricingRuleController(obj, updatedCart, cart, setCart, null, orderType, true, coupon.couponCode, coupon.referenceId, coupon.mPricingCouponId, coupon.mPricingruleId);
            })
          );
        }

        const allDiscounts = [...(updatedCart?.manualDiscountData?.lineLevelDiscount || []), ...(updatedCart?.manualDiscountData?.totalLevelDiscount || [])].sort(
          (a, b) => a.sequenceNo - b.sequenceNo
        );
        const uniqueDiscounts = allDiscounts.reduce((acc, item) => {
          if (!acc.some((discount) => discount.pricingRule === item.pricingRule)) acc.push(item);
          return acc;
        }, []);

        uniqueDiscounts.forEach(async (item) => {
          const pricingRule = manualDiscountTypes.find((md) => md.mPricingrulesId === item.pricingRule);
          if (pricingRule) {
            if (pricingRule.type === "FD" || pricingRule.type === "PD") {
              updatedCart.items.forEach(async (cartItem) => {
                const isLineLevelDiscount = updatedCart?.manualDiscountData?.lineLevelDiscount?.some((d) => d.pricingRule === item.pricingRule);
                let allDiscounts = cartItem?.allDiscounts || [];
                let matchedRuleIndex = allDiscounts.findIndex((d) => d.mPricingruleId === pricingRule.mPricingrulesId);
                if (isLineLevelDiscount && matchedRuleIndex !== -1) {
                  const applyDiscount = pricingRule.type === "FD" ? CheckoutFlatDiscount : CheckoutPercentageDiscount;
                  cartItem.priority = 0;
                  updatedCart = await applyDiscount(cartItem, pricingRule, setCart, updatedCart, orderType, updatedCart, item.discountValue);
                }
              });
            }
            if (pricingRule.type === "TD") {
              await CheckoutTotalManualDiscount(pricingRule, setCart, updatedCart, orderType, updatedCart, item.discountValue);
            }
          }
        });

        if (cart.totalBillDicount) {
          const matchingPricingRules = findPricingRuleById(cart.totalBillDicount);
          await processBillDiscounts(matchingPricingRules[0], cartObj, true);
          updatedCart = useCartStore.getState().cart;
        }

        if (cart.manualDiscountApplied && cart.manualDiscountApplied !== 0 && cart.discountType === "TD") {
          setLoader(true);
          setTimeout(async () => {
            await processTotalManualDiscount(cart.manualDiscountApplied);
            setLoader(false);
          }, 200);
        }

        setCart(updatedCart);
        return true;
      }
    } catch (error) {
      console.error("Submit error:", error);
      message.error(`${messages.info.submit_error}`);
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [
    submitting,
    customMixItems,
    groupName,
    buildCartObject,
    existingGroupItems,
    selectedCustomMix,
    cart,
    groupQty,
    taxIncludeFlag,
    precision,
    orderType,
    manualDiscountTypes,
    setCart,
    processBillDiscounts,
    setLoader,
    processTotalManualDiscount,
  ]);

  const resetState = useCallback(() => {
    setCustomMixItems([]);
    setGroupName("Custom Mix");
    setGroupQty(1);
    setExistingGroupItems(false);
    setSearchQuery("");
    setStockFailedItems([]);
    localStorage.removeItem("customMix");
    localStorage.removeItem("productSegment");
    setCompleteSaleStockData([]);
  }, [setCustomMixItems, setGroupName, setGroupQty, setExistingGroupItems, setCompleteSaleStockData]);

  return {
    // State
    productData,
    searchQuery,
    setSearchQuery,
    currentPage,
    setCurrentPage,
    filteredProducts,
    submitting,
    paginatedData,
    totalPages,
    precision,
    stockFailedItems,

    // Handlers
    handlePreviousPage,
    handleNextPage,
    handleIncrease,
    handleDecrease,
    handleDelete,
    handleQuantityChange,
    handleGroupQtyChange,
    handleSubmit,
    resetState,
    shouldOpenWeightPopup: needsPopup,
    openWeightPopupForGrouped,
    // Integrated weight popup (ProductsPopup-style, non-alternate UOM)
    integratedWeightProduct,
    integratedWeightPopupOpen,
    integratedWeightInputRef,
    handleIntegratedWeightTotalQty,
    handleIntegratedWeightInputChange,
    handleIntegratedWeightConfirm,
    handleIntegratedWeightCancel,
  };
};

export default useCustomMix;
